import { useCallback } from 'react'

import { EmptyState } from '@/components/ui/StateMessage'
import { formatMoney, pluralize } from '@/lib/format'

import styles from './VendorRows.module.css'

export interface VendorRowData {
  who: string
  amt: number
  n: number
}

function VendorRow({
  payee,
  maxAmt,
  onActivate,
}: {
  payee: VendorRowData
  maxAmt: number
  onActivate: (who: string) => void
}) {
  const handleClick = useCallback(() => onActivate(payee.who), [onActivate, payee.who])
  const pct = Math.min(100, (Math.abs(payee.amt) / maxAmt) * 100)
  return (
    <button
      type="button"
      className={styles.row}
      onClick={handleClick}
      aria-label={`${payee.who}: ${formatMoney(payee.amt)} across ${payee.n} ${pluralize(payee.n, 'transaction')}. View transactions`}
    >
      <span className={styles.name} title={payee.who}>
        {payee.who}
      </span>
      <svg className={styles.track} aria-hidden="true" focusable="false">
        <rect
          className={payee.amt < 0 ? styles.fillNeg : styles.fill}
          x="0"
          y="0"
          width={`${pct}%`}
          height="100%"
          rx="3"
        />
      </svg>
      <span className={`${styles.figures} num`}>
        {formatMoney(payee.amt, { compact: true })}{' '}
        <span className="text-faint">
          · {payee.n} {pluralize(payee.n, 'txn')}
        </span>
      </span>
    </button>
  )
}

export function VendorRows({ payees, onActivate }: { payees: VendorRowData[]; onActivate: (who: string) => void }) {
  if (payees.length === 0) return <EmptyState>No vendor-tagged transactions in this period.</EmptyState>
  const maxAmt = Math.max(1, ...payees.map((p) => Math.abs(p.amt)))
  return (
    <ul className={styles.rows}>
      {payees.map((payee) => (
        <li key={payee.who}>
          <VendorRow payee={payee} maxAmt={maxAmt} onActivate={onActivate} />
        </li>
      ))}
    </ul>
  )
}
